import { Floor } from "shared/types/Floor"
import { ProductPosition } from "shared/types/ProductPosition"
import {
  FloorTypename,
  ProductPositionTypename,
  Project,
  ProjectTypename,
  RoomTypename
} from "shared/types/Project"
import { Room } from "shared/types/Room"
import {
  AttachmentEntities,
  ProjectElement
} from "shared/types/__generated__/graphql"

import { RestLink } from "apollo-link-rest"

type PatchableParent = {
  id: string
  type: string
  parent?: PatchableParent | null
}

type PatchedParent = {
  id: string
  __typename: string
  parent?: PatchedParent | null
}

type PatchableEntity = ProjectElement & {
  type: string
  parent?: PatchableParent | null
}
type PatchableAttachmentEntities = Omit<AttachmentEntities, "entities"> & {
  entities: PatchableEntity[]
}

function patchParentChain(parent: PatchableParent): PatchedParent {
  return {
    ...parent,
    __typename: parent.type,
    parent: parent.parent ? patchParentChain(parent.parent) : parent.parent
  }
}

function patchEntityType(
  entity: PatchableEntity,
  outerType: string,
  patchDeeper: RestLink.FunctionalTypePatcher,
  context: RestLink.TypePatcherContext
): ProjectElement {
  if (entity.type === ProjectTypename) {
    return patchDeeper(
      { ...(entity as Project), __typename: ProjectTypename },
      outerType,
      patchDeeper,
      context
    )
  }

  if (entity.type === FloorTypename) {
    const floor = entity as Floor & { parent?: PatchableParent | null }
    return patchDeeper(
      {
        ...floor,
        __typename: FloorTypename,
        parent: floor.parent ? patchParentChain(floor.parent) : floor.parent
      },
      outerType,
      patchDeeper,
      context
    )
  }

  if (entity.type === RoomTypename) {
    const room = entity as Room & { parent?: PatchableParent | null }
    return patchDeeper(
      {
        ...room,
        __typename: RoomTypename,
        parent: room.parent ? patchParentChain(room.parent) : room.parent
      },
      outerType,
      patchDeeper,
      context
    )
  }

  const productPosition = entity as ProductPosition & {
    parent?: PatchableParent | null
  }
  return patchDeeper(
    {
      ...productPosition,
      __typename: ProductPositionTypename,
      parent: productPosition.parent
        ? patchParentChain(productPosition.parent)
        : productPosition.parent
    },
    outerType,
    patchDeeper,
    context
  )
}

export function patchAttachmentEntitiesType(
  data: PatchableAttachmentEntities,
  outerType: string,
  patchDeeper: RestLink.FunctionalTypePatcher,
  context: RestLink.TypePatcherContext
): AttachmentEntities {
  return {
    ...data,
    __typename: "AttachmentEntities",
    entities: data.entities.map(entity =>
      patchEntityType(entity, outerType, patchDeeper, context)
    )
  }
}
